import * as React from "react";
import { styled } from "styled-components";
import { useStore } from "@ludens-reklame/rubics-v3-react/store/hooks";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import { Constrainer } from "../../internal-components/Layout.js";
import { ThemeSettings } from "../../types/theme.js";
import { createImageProps } from "../../hooks/useImageProps.js";
import { createLinkProps, useLinkProps } from "../../hooks/useLinkProps.js";
import { RichText } from "../../internal-components/RichText.js";
import { FooterColumnsGraphic } from "../../types/theme.js";

export const Footer: React.FC = () => {
  const store = useStore<ThemeSettings>();
  const columns = store.themeSettings.footerColumns || [];
  const links = store.themeSettings.links || [];

  return (
    <StyledFooter>
      <FooterContentBackground>
        <FooterContent>
          {columns.map((column, index) => (
            <Column key={index}>
              {column.text && (
                <RichText dangerouslySetInnerHTML={{ __html: column.text }} />
              )}
              {column.graphics?.map((graphic, index) => (
                <Graphic className="graphic" key={index} graphic={graphic} />
              ))}
            </Column>
          ))}
        </FooterContent>
      </FooterContentBackground>
      <FooterBottomContent>
        {links
          .filter((x) => x.link)
          .map((link, index) => (
            <React.Fragment key={index}>
              <a {...createLinkProps(link.link!)}>{link.link!.title}</a>
              {links.length - 1 !== index && <>&nbsp;|&nbsp;</>}
            </React.Fragment>
          ))}
      </FooterBottomContent>
    </StyledFooter>
  );
};

interface GraphicProps {
  graphic: FooterColumnsGraphic;
  className: string;
}

const Graphic: React.FC<GraphicProps> = ({ graphic, className }) => {
  const type = graphic.imageType || "logo";
  const image = React.useMemo(() => {
    if (graphic.image) {
      return (
        <StyledGraphic
          className={`footer-image--${type}`}
          {...createImageProps(
            graphic.image,
            {
              width: type === "icon" ? 40 : 320,
              height: type === "icon" ? 40 : 320,
              fit: "cover",
            },
            "lazy"
          )}
        />
      );
    }

    return null;
  }, [graphic, type]);

  if (!image) return null;
  const link = useLinkProps(graphic.link);

  if (link) {
    return (
      <a className={className} {...link}>
        {image}
      </a>
    );
  }

  return image;
};

const StyledFooter = styled.footer`
  display: flex;
  flex-direction: column;
`;

const FooterContentBackground = styled.div`
  background: var(--footerBackground);
  color: var(--onFooterBackground);
`;

const FooterContent = styled(Constrainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.l};
  gap: ${({ theme }) => theme.spacing.m};

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: ${({ theme }) => theme.spacing.xl};
    justify-content: space-between;
    flex-direction: row;
    align-items: initial;
  }

  > div {
    max-width: 300px;
    width: 100%;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0 0 ${({ theme }) => theme.spacing.s} 0 !important;
  }

  a {
    color: var(--onFooterBackground);
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const StyledGraphic = styled.img<{ $type?: "icon" | "logo" }>`
  max-width: 100%;

  &.footer-image--icon {
    width: 30px;
  }

  &.footer-image--logo {
    height: 160px;
  }
`;

const FooterBottomContent = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.m};
  background: #fff;
  color: var(--swatch2);

  a {
    color: var(--swatch2);
  }
`;

const Column = styled.div`
  .graphic {
    margin-right: ${(props) => props.theme.spacing.s};
    margin-bottom: ${(props) => props.theme.spacing.xs};
    margin-top: ${(props) => props.theme.spacing.xs};
    display: inline-block;
  }
`;
